import React from 'react';
import { Heart, Cloud, Sun, CloudRain, Wind } from 'lucide-react';
import { TaraCard, TaraCardHeader, TaraCardTitle, TaraCardContent } from '../ui/TaraCard';
import TaraBadge from '../ui/TaraBadge';

const RelationshipWeather = () => {
  // Emotional graph - radial chart
  const emotionalTraits = {
    attachment: 75,
    empathy: 82,
    intuition: 78,
    independence: 65,
    trust: 72,
    passion: 68,
  };

  // Weekly weather alerts
  const weeklyAlerts = [
    {
      day: 'Today',
      weather: 'sunny',
      icon: Sun,
      title: 'High Harmony Day',
      message: 'Venus well-placed. Perfect for deep conversations.',
      color: '#10b981',
    },
    {
      day: 'Tomorrow',
      weather: 'cloudy',
      icon: Cloud,
      title: 'Mild Tension',
      message: 'Moon transit suggests minor misunderstandings. Stay patient.',
      color: '#f59e0b',
    },
    {
      day: 'Friday',
      weather: 'rainy',
      icon: CloudRain,
      title: 'Emotional Storm',
      message: 'Mars aspect creates friction. Avoid arguments.',
      color: '#ef4444',
    },
    {
      day: 'Sunday',
      weather: 'windy',
      icon: Wind,
      title: 'Communication Peak',
      message: 'Mercury direct. Great day for important discussions.',
      color: '#6366f1',
    },
  ];

  // Relationship phases
  const phases = [
    { period: '2024-2025', phase: 'Deepening', color: '#ec4899' },
    { period: '2026-2027', phase: 'Testing', color: '#f59e0b' },
    { period: '2028-2030', phase: 'Commitment', color: '#10b981' },
  ];

  return (
    <div className="space-y-6">
      {/* Emotional Graph */}
      <TaraCard variant="cosmic">
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            Emotional Intelligence Map
          </TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Based on Moon + Venus + 7th house
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-4">
            {Object.entries(emotionalTraits).map(([key, value]) => (
              <div key={key}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-stone-700 capitalize">
                    {key}
                  </span>
                  <span className="text-sm font-medium text-pink-600">
                    {value}%
                  </span>
                </div>
                <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-pink-400 to-pink-600 transition-all duration-500"
                    style={{ width: `${value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Weekly Weather Alerts */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle>Relationship Weather This Week</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Day-by-day emotional forecast
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-3">
            {weeklyAlerts.map((alert) => {
              const Icon = alert.icon;
              return (
                <div
                  key={alert.day}
                  className="p-4 rounded-lg border-2"
                  style={{ borderColor: `${alert.color}40`, backgroundColor: `${alert.color}10` }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${alert.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: alert.color }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-serif font-medium text-stone-900">
                          {alert.title}
                        </span>
                        <span className="text-xs font-medium text-stone-600">
                          {alert.day}
                        </span>
                      </div>
                      <p className="text-sm text-stone-600">{alert.message}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Relationship Phases */}
      <TaraCard variant="sacred">
        <TaraCardHeader>
          <TaraCardTitle>Relationship Phases Ahead</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Long-term cycle predictions
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-3">
            {phases.map((phase) => (
              <div
                key={phase.period}
                className="flex items-center justify-between p-3 bg-white/50 rounded-lg backdrop-blur-sm"
              >
                <div>
                  <div className="font-medium text-stone-900 text-sm">{phase.period}</div>
                  <div className="text-xs text-stone-600">{phase.phase} Phase</div>
                </div>
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: phase.color }}
                />
              </div>
            ))}
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Connection Tip */}
      <TaraCard>
        <TaraCardContent className="p-5 bg-gradient-to-br from-pink-50 to-white">
          <div className="text-center">
            <div className="text-3xl mb-2">💝</div>
            <h4 className="font-serif font-medium text-stone-900 mb-2">
              Today's Connection Tip
            </h4>
            <p className="text-sm text-stone-700">
              Venus in your 7th house. Express gratitude to your partner today. Small gestures create big bonds.
            </p>
          </div>
        </TaraCardContent>
      </TaraCard>
    </div>
  );
};

export default RelationshipWeather;